import React, { useState, useEffect, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Grid,
    Box,
    CircularProgress,
    Alert,
} from "@mui/material";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import apiRequest from "../../apiRequest";
import { endPoints } from "../../apis";

/**
 * CKEditor Plugin to make placeholders atomic (non-editable units)
 */
function PlaceholderPlugin(editor) {
    // 1. Register 'placeholder' in the schema as an atomic object
    editor.model.schema.register('placeholder', {
        allowWhere: '$text',
        isObject: true,
        allowAttributes: ['data-key']
    });

    // 2. Upcast: Convert <span class="placeholder-protected"> to model 'placeholder'
    editor.conversion.for('upcast').elementToElement({
        view: {
            name: 'span',
            classes: 'placeholder-protected'
        },
        model: (viewElement, { writer }) => {
            return writer.createElement('placeholder', {
                'data-key': viewElement.getAttribute('data-key')
            });
        }
    });

    // 3. Downcast (Editing): Convert model 'placeholder' back to HTML in the editor
    editor.conversion.for('editingDowncast').elementToElement({
        model: 'placeholder',
        view: (modelElement, { writer }) => {
            const key = modelElement.getAttribute('data-key');
            const span = writer.createContainerElement('span', {
                class: 'placeholder-protected',
                contenteditable: 'false'
            });
            writer.insert(writer.createPositionAt(span, 0), writer.createText(`{{${key}}}`));
            return span;
        }
    });

    // 4. Downcast (Data): Ensure editor.getData() returns raw {{placeholder}}
    editor.conversion.for('dataDowncast').elementToElement({
        model: 'placeholder',
        view: (modelElement, { writer }) => {
            return writer.createText(`{{${modelElement.getAttribute('data-key')}}}`);
        }
    });
}

// Global utility to wrap raw text with protection spans before editor load
const toProtectedHTML = (html) => {
    if (!html) return "";
    return html.replace(/\{\{([a-zA-Z0-9_]+)\}\}/g, '<span class="placeholder-protected" data-key="$1" contenteditable="false">{{$1}}</span>');
};

// Global utility to strip protection spans before API call (Fallback)
const toRawFormat = (html) => {
    if (!html) return "";
    const container = document.createElement("div");
    container.innerHTML = html;
    const tokens = container.querySelectorAll(".placeholder-protected");
    tokens.forEach((token) => {
        token.replaceWith(token.innerText);
    });
    return container.innerHTML;
};


const AVAILABLE_PLACEHOLDERS = [
    { label: "Customer Name", key: "customer_name" },
    { label: "Customer Address", key: "customer_address" },
    { label: "Customer Phone", key: "customer_phone" },
    { label: "Customer Email", key: "customer_email" },
    { label: "Company Name", key: "company_name" },
    { label: "Company Address", key: "company_address" },
    { label: "Company Phone", key: "company_phone" },
    { label: "Company Email", key: "company_email" },
    { label: "Vehicle Description", key: "vehicle_description" },
    { label: "Service Description", key: "service_description" },
    { label: "Pickup Location", key: "pickup_location" },
    { label: "Dropoff Location", key: "dropoff_location" },
    { label: "Response Time", key: "response_time" },
    { label: "Towing Fee", key: "towing_fee" },
    { label: "Payment Terms", key: "payment_terms" },
];

function AddAgreementModal({ open, onClose, onSuccess, editingData }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const editorRef = useRef(null);
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        html_content: "",
    });

    useEffect(() => {
        if (open) {
            if (editingData) {
                setFormData({
                    name: editingData.name || "",
                    description: editingData.description || "",
                    html_content: toProtectedHTML(editingData.html_content || ""),
                });
            } else {
                setFormData({
                    name: "",
                    description: "",
                    html_content: "",
                });
            }
            setError(null);
        }
    }, [open, editingData]);

    const handleInsertVariable = (key) => {
        if (editorRef.current) {
            const editor = editorRef.current;
            editor.model.change((writer) => {
                const placeholderElement = writer.createElement("placeholder", {
                    "data-key": key,
                });
                editor.model.insertContent(placeholderElement);
            });
        }
    };

    const handleSubmit = async () => {
        if (!formData.name || !formData.html_content) {
            setError("Name and Content are required.");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const payload = {
                ...formData,
                html_content: toRawFormat(formData.html_content),
            };

            let response;
            if (editingData) {
                response = await apiRequest.put(`${endPoints.UPDATE_AGREEMENT_TEMPLATE}/${editingData.id}`, payload);
            } else {
                response = await apiRequest.post(endPoints.ADD_AGREEMENT_TEMPLATE, payload);
            }

            if (response.success) {
                if (onSuccess) onSuccess(editingData ? "Agreement template updated successfully." : "Agreement template added successfully.");
                onClose();
            } else {
                setError(response.message || `Failed to ${editingData ? "update" : "add"} template.`);
            }
        } catch (err) {
            console.error("Error adding template:", err);
            setError("An error occurred while adding the template.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>{editingData ? "Edit Agreement Template" : "Add New Agreement Template"}</DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <TextField
                            label="Name"
                            fullWidth
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            margin="dense"
                            disabled={loading}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            label="Description"
                            fullWidth
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            margin="dense"
                            disabled={loading}
                        />
                    </Grid>
                    <Grid item xs={12} md={9}>
                        <Box sx={{
                            "& .ck.ck-editor__main": {
                                minHeight: "300px",
                            },
                            "& .ck-content": {
                                minHeight: "300px",
                            },
                            "& .ck.ck-toolbar": {
                                position: "sticky !important",
                                top: 0,
                                zIndex: 1100,
                                backgroundColor: "white !important",
                                borderBottom: "1px solid #ccc",
                            },
                            "& .ck.ck-content code": {
                                backgroundColor: "#f5f5f5",
                                padding: "2px 4px",
                                borderRadius: "4px",
                                color: "#d63384",
                                fontFamily: "monospace",
                                fontSize: "0.9em",
                            },
                            "& .ck.ck-content .placeholder-protected": {
                                color: "#1A73E8",
                                fontWeight: "bold",
                                background: "#e8f0fe",
                                border: "1px solid #1A73E8",
                                padding: "0 4px",
                                borderRadius: "4px",
                                display: "inline-block",
                                userSelect: "none",
                                cursor: "default",
                            },
                            "& .ck.ck-content ul": {
                                paddingLeft: "20px",
                                listStyleType: "disc",
                            },
                            "& .ck.ck-content ol": {
                                paddingLeft: "20px",
                                listStyleType: "decimal",
                            },
                        }}>
                            <CKEditor
                                editor={ClassicEditor}
                                data={formData.html_content}
                                onReady={(editor) => {
                                    editorRef.current = editor;
                                }}
                                onChange={(event, editor) => {
                                    const data = editor.getData();
                                    setFormData({ ...formData, html_content: data });
                                }}
                                config={{
                                    extraPlugins: [PlaceholderPlugin],
                                    htmlSupport: {
                                        allow: [
                                            {
                                                name: "span",
                                                classes: ["placeholder-protected"],
                                                attributes: ["contenteditable", "data-key"]
                                            }
                                        ]
                                    }
                                }}
                                disabled={loading}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Box sx={{
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            p: 1,
                            height: "calc(100vh - 250px)", // Fixed height relative to viewport
                            position: "sticky",
                            top: 10,
                            overflowY: "auto",
                            backgroundColor: "#f9f9f9",
                            /* Hide scrollbar for Chrome, Safari and Opera */
                            "&::-webkit-scrollbar": {
                                display: "none"
                            },
                            /* Hide scrollbar for IE, Edge and Firefox */
                            msOverflowStyle: "none",  /* IE and Edge */
                            scrollbarWidth: "none",  /* Firefox */
                        }}>
                            <Box sx={{
                                mb: 1,
                                fontWeight: "bold",
                                textAlign: "center",
                                position: "sticky",
                                top: 0,
                                backgroundColor: "#f9f9f9",
                                zIndex: 1,
                                py: 1,
                                borderBottom: "1px solid #eee",
                                color: "#1A73E8"
                            }}>
                                Variables
                            </Box>
                            <Grid container spacing={1}>
                                {AVAILABLE_PLACEHOLDERS.map((ph) => (
                                    <Grid item xs={12} key={ph.key}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            onClick={() => handleInsertVariable(ph.key)}
                                            sx={{
                                                textTransform: "none",
                                                fontSize: "0.75rem",
                                                justifyContent: "flex-start",
                                                backgroundColor: "white",
                                                borderColor: "#1A73E8",
                                                color: "#1A73E8",
                                                "&:hover": {
                                                    backgroundColor: "#e8f0fe",
                                                }
                                            }}
                                        >
                                            {ph.label}
                                        </Button>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="secondary" disabled={loading}>
                    Cancel
                </Button>
                <Button onClick={handleSubmit} variant="contained" color="info" sx={{ color: "white" }} disabled={loading}>
                    {loading ? <CircularProgress size={24} color="inherit" /> : (editingData ? "Update" : "Submit")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default AddAgreementModal;
